import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  FilterAlt,
  Clear,
  Language,
  Category,
  List,
  LocalShipping,
  Event,
  Numbers,
  Scale,
  Link,
  Assessment,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useLocalizedData } from '../hooks/useLocalizedData';
import countries from "../data/countries";
import categories from "../data/categories.json";
import subcategories from "../data/subcategories.json";
import types from "../data/types.json";
import donations from "../data/donations.json";

const DonationCard = ({ item, sx }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { getLocalizedValue } = useLocalizedData();

  // Helper component to show label and value clearly
  const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      {icon &&
        React.cloneElement(icon, {
          color: "action",
          sx: { mr: 1, fontSize: "1.1rem" },
        })}
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, mr: 1, minWidth: 100 }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: theme.shadows[3],
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[6],
        },
        ...sx,
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Country with Flag and Name */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          {countries[item.country]?.flag && (
            <Avatar
              src={countries[item.country].flag}
              alt={item.country}
              variant="square"
              sx={{ width: 36, height: 36, mr: 1.5,  img: { objectFit: "contain" } }}
            />
          )}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {getLocalizedValue(countries[item.country], 'name') || item.country}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Entity names */}
        <InfoRow label={t("Entity")} value={getLocalizedValue(item, 'entity')} />

        <Divider sx={{ my: 2 }} />

        {/* Date, Quantity, Weight */}
        <InfoRow label={t("Date")} value={item.date} icon={<Event />} />
        <InfoRow
          label={t("Quantity")}
          value={`${item.quantity} ${t("units")}`}
          icon={<Numbers />}
        />
        <InfoRow
          label={t("Weight")}
          value={`${item.weight_ton} ${t("tons")}`}
          icon={<Scale />}
        />

        <Divider sx={{ my: 2 }} />

        {/* Categories */}
        <InfoRow
          label={t("Category")}
          value={
            getLocalizedValue(categories.find((c) => c.id === item.category_id), 'category') || "-"
          }
          icon={<Category />}
        />
        <InfoRow
          label={t("Subcategory")}
          value={
            getLocalizedValue(subcategories.find((s) => s.id === item.subcategory_id), 'subcategory') || "-"
          }
          icon={<List />}
        />
        <InfoRow
          label={t("Type")}
          value={
            getLocalizedValue(types.find((t) => t.id === item.type_id), 'type') || "-"
          }
          icon={<LocalShipping />}
        />

        {item.source && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Link />}
            href={item.source}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            sx={{ mt: 3 }}
          >
            {t("View Source")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const FilterSection = ({ filters, handleChange, handleReset }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { getLocalizedValue } = useLocalizedData();

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mb: 4,
        borderRadius: 3,
        background: theme.palette.grey[100],
      }}
    >
      {/* Header with title and clear button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          <FilterAlt sx={{ verticalAlign: "middle", mr: 1 }} />
          {t("Filter Donations")}
        </Typography>
        <Button
          onClick={handleReset}
          startIcon={<Clear />}
          variant="outlined"
          size="small"
        >
          {t("Clear Filters")}
        </Button>
      </Box>

      {/* Full-width search field */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label={t("Search Entity")}
          variant="outlined"
          value={filters.search}
          onChange={(e) => handleChange("search", e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: "action.active", mr: 1 }} />,
            sx: {
              height: "56px",
              "& input": {
                padding: "16.5px 14px",
              },
            },
          }}
        />
      </Box>

      {/* 4 dropdowns in a row */}
      <Box sx={{ width: "100%" }}>
        <Grid
          container
          spacing={2}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
          }}
        >
          {/* Category */}
          <Grid item>
            <FormControl fullWidth>
              <InputLabel>{t("Category")}</InputLabel>
              <Select
                value={filters.category}
                label={t("Category")}
                onChange={(e) => handleChange("category", e.target.value)}
                sx={{
                  height: "56px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "16px",
                    paddingBottom: "16px",
                  },
                }}
              >
                <MenuItem value="">{t("All Categories")}</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {getLocalizedValue(cat, 'category')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Subcategory */}
          <Grid item>
            <FormControl fullWidth>
              <InputLabel>{t("Entity")}</InputLabel>
              <Select
                value={filters.entity_en}
                label={t("Entity")}
                onChange={(e) => handleChange("entity_en", e.target.value)}
                // disabled={!filters.category}
                sx={{
                  height: "56px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "16px",
                    paddingBottom: "16px",
                  },
                }}
              >
                <MenuItem value="">{t("All Entities")}</MenuItem>
                {[
                  "High Relief Commission",
                  "Government Emergency Committee",
                  "South Council",
                ].map((sub, index) => (
                  <MenuItem key={index} value={sub}>
                    {t(sub)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Type */}
          <Grid item>
            <FormControl fullWidth>
              <InputLabel>{t("Type")}</InputLabel>
              <Select
                value={filters.type}
                label={t("Type")}
                onChange={(e) => handleChange("type", e.target.value)}
                sx={{
                  height: "56px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "16px",
                    paddingBottom: "16px",
                  },
                }}
              >
                <MenuItem value="">{t("All Types")}</MenuItem>
                {types.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {getLocalizedValue(type, 'type')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Country */}
          <Grid item>
            <FormControl fullWidth>
              <InputLabel>{t("Donor")}</InputLabel>
              <Select
                value={filters.country}
                label={t("Donor")}
                onChange={(e) => handleChange("country", e.target.value)}
                sx={{
                  height: "56px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "16px",
                    paddingBottom: "16px",
                  },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {selected && countries[selected]?.flag && (
                      <Avatar
                        src={countries[selected].flag}
                        alt={selected}
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                    )}
                    {getLocalizedValue(countries[selected], 'name') || selected || t("All Donors")}
                  </Box>
                )}
              >
                <MenuItem value="">{t("All Donors")}</MenuItem>
                {[...new Set(donations.map((d) => d.country))].map(
                  (country) => (
                    <MenuItem key={country} value={country}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {countries[country]?.flag && (
                          <Avatar
                            src={countries[country].flag}
                            alt={country}
                            sx={{ width: 24, height: 24, mr: 1 }}
                          />
                        )}
                        {getLocalizedValue(countries[country], 'name') || country}
                      </Box>
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const StatsBar = ({ filteredData }) => {
  const { t } = useTranslation();
  const totalDonations = filteredData.length;

  // Safely calculate total weight by ensuring each item's weight is a number
  const totalWeight = filteredData.reduce((sum, item) => {
    const weight = parseFloat(item.weight_ton);
    return sum + (isNaN(weight) ? 0 : weight);
  }, 0);

  const uniqueCountries = new Set(filteredData.map((item) => item.country))
    .size;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-around",
        flexWrap: "wrap",
        gap: 2,
        mb: 4,
        "& > *": {
          flex: "1 1 200px",
        },
      }}
    >
      <Paper elevation={2} sx={{ p: 2, borderRadius: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          {t("Total Donations")}
        </Typography>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {totalDonations}
        </Typography>
      </Paper>
      <Paper elevation={2} sx={{ p: 2, borderRadius: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          {t("Total Weight")}
        </Typography>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "secondary.main" }}
        >
          {typeof totalWeight === "number" ? totalWeight.toFixed(1) : "N/A"}{" "}
          {t("tons")}
        </Typography>
      </Paper>
      <Paper elevation={2} sx={{ p: 2, borderRadius: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          {t("Donors")}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "info.main" }}>
          {uniqueCountries}
        </Typography>
      </Paper>
    </Box>
  );
};

export default function DonationDashboard() {
  const { t } = useTranslation();
  const [filteredData, setFilteredData] = useState(donations);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: "",
    subcategory: "",
    type: "",
    country: "",
    search: "",
    entity_en: "",
  });
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    // Simulate loading for better UX
    const timer = setTimeout(() => {
      const result = donations.filter((d) => {
        return (
          (filters.category === "" || d.category_id === filters.category) &&
          (filters.subcategory === "" ||
            d.subcategory_id === filters.subcategory) &&
          (filters.type === "" || d.type_id === filters.type) &&
          (filters.country === "" || d.country === filters.country) &&
          (filters.entity_en === "" || d.entity_en === filters.entity_en) &&
          (filters.search === "" ||
            d.entity_en.toLowerCase().includes(filters.search.toLowerCase()))
        );
      });
      setFilteredData(result);
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    // Reset subcategory when category changes
    if (field === "category") {
      setFilters((prev) => ({ ...prev, subcategory: "" }));
    }
  };

  const handleReset = () => {
    setFilters({
      category: "",
      subcategory: "",
      type: "",
      country: "",
      search: "",
      entity_en: "",
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // You could implement different views here
  };

  return (
    <Box className="animate-fade-in-up">
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h2" 
          className="heading-primary"
          sx={{ 
            textAlign: 'center',
            mb: 3,
            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t("Lebanon Aid Tracker")}
        </Typography>

        {/* Gherbal Initiative Branding - Between title and description */}
        <Box 
          component="a"
          href="https://elgherbal.org/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            gap: 2,
            flexWrap: 'wrap',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            borderRadius: 2,
            p: 1,
            '&:hover': {
              transform: 'translateY(-2px)',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
            }
          }}
        >
          <img 
            src="/gh.png" 
            alt="Gherbal Initiative" 
            style={{ 
              height: '80px',
              width: 'auto',
              opacity: 0.8,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
            }}
          />
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            {t("Powered by Gherbal Initiative")}
          </Typography>
        </Box>

        <Typography 
          variant="h6" 
          className="text-elegant"
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mb: 3
          }}
        >
          {t("Aid Distribution Dashboard")}
        </Typography>
        
        {/* Gherbal Initiative Context */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            maxWidth: '900px', 
            mx: 'auto',
            background: 'linear-gradient(135deg, rgba(12, 159, 193, 0.05) 0%, rgba(77, 182, 209, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 3,
            mb: 3
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: 'text.primary',
              lineHeight: 1.7,
              fontSize: '1.1rem',
              mb: 2
            }}
          >
            {t("dashboard_description_part1")}{' '}
            <Typography 
              component="a"
              href="https://elgherbal.org/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }
              }}
            >
              {t("Gherbal Initiative")}
            </Typography>
            {' '}{t("dashboard_description_part2")}
          </Typography>
          
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              component={RouterLink}
              to="/analysis"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(12, 159, 193, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              startIcon={<Assessment />}
            >
              {t("View Detailed Analysis")}
            </Button>
          </Box>
        </Paper>
      </Box>
        <FilterSection
          filters={filters}
          handleChange={handleChange}
          handleReset={handleReset}
        />

        <StatsBar filteredData={filteredData} />

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3 }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={t("All Donations")} />
          {/* <Tab label="Recent" />
          <Tab label="Largest" /> */}
        </Tabs>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h5" color="text.secondary">
              {t("No donations match your filters")}
            </Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={handleReset}>
              {t("Clear Filters")}
            </Button>
          </Paper>
        ) : (
          <Grid
            container
            spacing={3}
            alignItems="center"
            justifyContent="center"
          >
            {filteredData.map((item, idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={idx}
                sx={{ display: "flex" }}
              >
                <DonationCard item={item} sx={{ flex: 1 }} />
              </Grid>
            ))}
          </Grid>
        )}
    </Box>
  );
}
